//libs\featureds\public\clients\src\lib\data-access\client-auth.service.ts
import { HttpClient, HttpContext } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { APP_CONFIG } from '@cardapio-online/config';
import { SKIP_ADMIN_AUTH } from '@cardapio-online/http';
import {
  AuthenticatedClient,
  ClientAuthRequest,
  ClientAuthResponse,
  ClientAuthSession,
} from '../models/client.interface';
import { Observable, tap } from 'rxjs';

type ClientTokenClaims = {
  exp?: number;
  tslug?: string;
};

@Injectable({
  providedIn: 'root',
})
export class ClientAuthService {
  private readonly http = inject(HttpClient);
  private readonly cfg = inject(APP_CONFIG);
  private readonly basePath = '/client-auth';
  private readonly publicContext = new HttpContext().set(SKIP_ADMIN_AUTH, true);
  private readonly storageKey = `menuhub:${this.cfg.tenantSlug}:client-auth`;

  private readonly sessionState = signal<ClientAuthSession | null>(null);

  readonly session = this.sessionState.asReadonly();
  readonly token = computed(() => this.sessionState()?.token ?? null);
  readonly client = computed(() => this.sessionState()?.client ?? null);
  readonly isAuthenticated = computed(() => !!this.sessionState()?.token);

  constructor() {
    this.hydrateFromStorage();
  }

  login(
    payload: ClientAuthRequest,
    clientDetails?: Partial<AuthenticatedClient>
  ): Observable<ClientAuthResponse> {
    return this.http
      .post<ClientAuthResponse>(`${this.basePath}/login`, payload, {
        context: this.publicContext,
      })
      .pipe(
        tap((response) => {
          const token = response?.data?.token?.trim() ?? '';

          if (!token) {
            throw new Error('Token de cliente ausente na resposta.');
          }

          const nextSession: ClientAuthSession = {
            token,
            client: {
              ...response.data.client,
              email: clientDetails?.email ?? response.data.client.email ?? null,
            },
          };

          this.setSession(nextSession);
        })
      );
  }

  logout(): void {
    this.clearSession();
  }

  getToken(): string | null {
    return this.token();
  }

  getSessionSnapshot(): ClientAuthSession | null {
    return this.sessionState();
  }

  updateClientDetails(details: Partial<AuthenticatedClient>): void {
    const current = this.sessionState();

    if (!current) {
      return;
    }

    this.setSession({
      ...current,
      client: {
        ...current.client,
        ...details,
      },
    });
  }

  private hydrateFromStorage(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    const rawValue = localStorage.getItem(this.storageKey);

    if (!rawValue) {
      return;
    }

    try {
      const parsed = JSON.parse(rawValue) as ClientAuthSession;

      if (!parsed?.token || !parsed?.client?.id) {
        this.clearSession();
        return;
      }

      if (this.isTokenExpired(parsed.token) || this.isTenantMismatch(parsed.token)) {
        this.clearSession();
        return;
      }

      this.sessionState.set(parsed);
    } catch {
      this.clearSession();
    }
  }

  private setSession(session: ClientAuthSession): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(this.storageKey, JSON.stringify(session));
    }

    this.sessionState.set(session);
  }

  private clearSession(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(this.storageKey);
    }

    this.sessionState.set(null);
  }

  private isTokenExpired(token: string): boolean {
    const claims = this.parseTokenClaims(token);

    if (!claims?.exp) {
      return false;
    }

    return claims.exp <= Math.floor(Date.now() / 1000);
  }

  private isTenantMismatch(token: string): boolean {
    const claims = this.parseTokenClaims(token);

    if (!claims?.tslug) {
      return false;
    }

    return claims.tslug !== this.cfg.tenantSlug;
  }

  private parseTokenClaims(token: string): ClientTokenClaims | null {
    try {
      const payload = token.split('.')[1];

      if (!payload) {
        return null;
      }

      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(
        normalized.length + ((4 - (normalized.length % 4)) % 4),
        '='
      );
      const decoded = atob(padded);

      return JSON.parse(decoded) as ClientTokenClaims;
    } catch {
      return null;
    }
  }
}
